import { createEnvironmentInjector, DestroyRef, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import {
  MESSAGE_INPUT_COLLABORATION_ID,
  MESSAGE_INPUT_SCREEN_RECORDING_ORIGIN,
  MessageInputService,
  ScreenRecordingOrigin,
} from '@libs/collaboration';
import { HistoryStackService } from '@libs/shared-services';
import { authActions } from '@libs/store';

/**
 * The personal assistant composer's own MessageInputServices. The root instance is shared by the collab screen and
 * support center composers, which clear it whenever they mount, so the assistant's draft cannot live there. A child
 * injector (same pattern as createVoiceChannel) gives each one an app-lifetime instance with its own undo history, so the
 * draft outlives the chat window being unmounted - the layout drops it on the support center route. That also keeps a
 * screen recording started from the assistant: it lands in the draft even if the window was closed while recording.
 *
 * There is one instance for each assistant collaboration. That collaboration belongs to one user at one site, so a draft
 * stays with the user and site it was written for: switching the site shows that site's own draft, and switching back
 * restores the previous one. Logging out drops every draft - the attachments often hold patient images, and the
 * workstation may be shared - so nothing of the previous user stays in memory.
 */
@Injectable({ providedIn: 'root' })
export class AssistantMessageInputRegistry {
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly injectors = new Map<string | undefined, EnvironmentInjector>();

  constructor() {
    // Logout navigates to the login route without a page reload, so this root singleton has to drop the drafts itself.
    inject(Actions)
      .pipe(ofType(authActions.onLogout), takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.clear());
  }

  public get(collaborationId: string | undefined): MessageInputService {
    let injector = this.injectors.get(collaborationId);
    if (!injector) {
      injector = createEnvironmentInjector(
        [
          MessageInputService,
          HistoryStackService,
          // Only recordings started from the assistant land in its draft, not the ones started from the collab screen.
          { provide: MESSAGE_INPUT_SCREEN_RECORDING_ORIGIN, useValue: ScreenRecordingOrigin.Assistant },
          { provide: MESSAGE_INPUT_COLLABORATION_ID, useValue: collaborationId },
        ],
        this.environmentInjector,
      );
      this.injectors.set(collaborationId, injector);
    }

    return injector.get(MessageInputService);
  }

  private clear(): void {
    for (const injector of this.injectors.values()) {
      const messageInputService = injector.get(MessageInputService);
      messageInputService.stopDictationIfActive();
      // Picked and recorded files are held through object URLs, which stay valid until revoked or the page reloads.
      for (const attachment of messageInputService.state().attachments.allAttachments) {
        if (attachment.url?.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.url);
        }
      }
      injector.destroy();
    }
    this.injectors.clear();
  }
}
